import React from 'react';

import noop from 'lodash/noop';

import { useDataAttributes } from '@lumapps/data-attributes';
import { CopyToClipboardButton } from '@lumapps/lumx-buttons/components/CopyToClipboardButton';
import { mdiThumbDown, mdiThumbDownOutline, mdiThumbUp, mdiThumbUpOutline } from '@lumapps/lumx/icons';
import { FlexBox, IconButton } from '@lumapps/lumx/react';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { AI } from '../../keys';

const DEFAULT_SCOPE = 'ai-reactions';

export interface AiReactionsProps {
    className?: string;
    /** Content to copy to clipboard */
    contentToCopy?: string;
    /** Whether the component is used in the micro-apps builder context, only for preview */
    isPreview?: boolean;
    /** Icon sizes */
    size?: 's' | 'm';
    copyButtonRef?: React.RefObject<HTMLButtonElement>;
    /** Current feedback state (controlled) */
    feedbackType?: 'like' | 'dislike' | null;
    /** Callback when feedback button clicked */
    onFeedbackClick: (feedbackType: 'like' | 'dislike') => void;
    /** Whether to disable buttons after feedback is given */
    disableAfterFeedback?: boolean;
    /** Whether to disable buttons (e.g., while loading) */
    isDisabled?: boolean;
    /** Custom scope for data attributes */
    scope?: string;
}

/**
 * Display buttons below an AI answer or message:
 * "Copy" which copies content to the clipboard
 * "Like" if the user is satisfied
 * "Dislike" if the user is unsatisfied with the given answer
 */
export const AiReactions = ({
    contentToCopy,
    size = 's',
    isPreview = false,
    className,
    copyButtonRef,
    feedbackType,
    onFeedbackClick,
    disableAfterFeedback = true,
    isDisabled = false,
    scope = DEFAULT_SCOPE,
}: AiReactionsProps) => {
    const { success: showSuccessNotification } = useNotification();
    const { translateKey } = useTranslate();

    const { get } = useDataAttributes(scope);

    const onCopySuccess = () => showSuccessNotification({ translate: GLOBAL.COPIED });

    const isLiked = feedbackType === 'like';
    const isDisliked = feedbackType === 'dislike';
    const buttonsDisabled = isDisabled || (disableAfterFeedback && Boolean(feedbackType));

    return (
        <FlexBox className={className} orientation="horizontal" hAlign="center">
            <IconButton
                emphasis="low"
                size={size}
                icon={isLiked ? mdiThumbUp : mdiThumbUpOutline}
                label={translateKey(AI.GOOD_RESPONSE)}
                /** if the component is used for preview in the micro-apps builder context, do not send event */
                onClick={isPreview ? noop : () => onFeedbackClick('like')}
                isDisabled={buttonsDisabled}
                isSelected={isLiked}
                {...get({ element: 'button', action: 'satisfied' })}
            />

            <IconButton
                emphasis="low"
                size={size}
                icon={isDisliked ? mdiThumbDown : mdiThumbDownOutline}
                label={translateKey(AI.BAD_RESPONSE)}
                /** if the component is used for preview in the micro-apps builder context, do not send event */
                onClick={isPreview ? noop : () => onFeedbackClick('dislike')}
                isDisabled={buttonsDisabled}
                isSelected={isDisliked}
                {...get({ element: 'button', action: 'unsatisfied' })}
            />

            {contentToCopy && (
                <CopyToClipboardButton
                    value={contentToCopy}
                    size={size}
                    onSuccess={onCopySuccess}
                    buttonRef={copyButtonRef}
                    {...get({ element: 'button', action: 'copy' })}
                />
            )}
        </FlexBox>
    );
};
